import { FC, MouseEventHandler, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import CodeUtil from '../../utils/CodeUtil';
import { CodeDataGridColumnHeader } from './components/CodeDataGridColumnHeader';
import { CodeDataGridTextColumn } from './components/CodeDataGridTextColumn';
import { CodeDataGridHeader } from './components/CodeDataGridHeader';
import { CodeDataGridFooter } from './components/CodeDataGridFooter';
import { ICodeDataGridActionButton, ICodeDataGridColumn, ICodeDataGridHeader, ICodeDataGridPagination, ICodeDataGridRow, ICodeDataGridSort } from './types';
import { CodeDataGridRowItem } from './components/CodeDataGridRowItem';

export type CodeDataGridProps = {
    datasource?: any[]
    className?: string
    marginContainer?: string
    striped?: boolean | undefined
    hoverable?: boolean
    multiselect?: boolean | undefined
    columns: ICodeDataGridColumn[]
    pagination?: ICodeDataGridPagination
    actions?: ICodeDataGridActionButton[]
    header?: ICodeDataGridHeader
    sort?: ICodeDataGridSort
    simplified?: boolean
    readonly?: boolean
    hiddenFooter?: boolean
    height?: string
    onRowSelect?: (item: ICodeDataGridRow) => void
    onRowsSelect?: (item: ICodeDataGridRow[]) => void
    onContextMenu?: MouseEventHandler | undefined;
}

export const onCustomFormatColumn = (item: any, property: string, type: "money" | "number" | "integer" | "percent" | "text" | "minutes" | "hours" = "text"): JSX.Element => {
    if (type === "money") {
        return <span>{CodeUtil.moneyFormat(CodeUtil.getPropertyValue(item, property) ?? 0, true)}</span>
    }

    if (type === "number") {
        return <span>{CodeUtil.moneyFormat(CodeUtil.getPropertyValue(item, property) ?? 0, false)}</span>
    }

    if (type === "integer") {
        return <span>{parseInt(CodeUtil.getPropertyValue(item, property) ?? "0")}</span>
    }

    if (type === "percent") {
        return <span>{CodeUtil.moneyFormat(CodeUtil.getPropertyValue(item, property) ?? 0, false)} %</span>
    }

    if (type === "minutes") {
        return <span>{CodeUtil.moneyFormat(CodeUtil.getPropertyValue(item, property) ?? 0, false)} min</span>
    }

    if (type === "hours") {
        return <span>{CodeUtil.moneyFormat(CodeUtil.getPropertyValue(item, property) ?? 0, false)} hrs</span>
    }

    return <span>{CodeUtil.getPropertyValue(item, property)}</span>
}


export const CodeDataGrid: FC<CodeDataGridProps> = (props) => {
    const [dataSource, setDataSource] = useState<any[] | undefined>(props.datasource);
    const [selectedRows, setSelectedRows] = useState<ICodeDataGridRow[]>([]);
    const [selectAll, setSelectAll] = useState<boolean>(false);
    const [sort, setSort] = useState<ICodeDataGridSort>(props.sort ?? { columnIndex: -1, direction: 'asc' })
    const [query, setQuery] = useState<string>('');
    const tableHeader = useRef<HTMLDivElement>(null);
    const minColumnCount: number = 10;

    const handleSortColumn = async (property: string, index: number, direction: 'asc' | 'desc') => {
        const source = props.datasource ?? dataSource;

        setSort({ ...sort, columnIndex: index, columnProperty: property, direction: direction });

        const sortedData = source?.sort((a, b) => CodeUtil.isNumeric(a[property]) ?
            (direction === 'asc' ? String(a[property]).padStart(10, "0").localeCompare(String(b[property]).padStart(10, "0")) : String(b[property]).padStart(10, "0").localeCompare(String(a[property]).padStart(10, "0"))) :
            (direction === 'asc' ? String(a[property]).localeCompare(b[property]) : String(b[property]).localeCompare(a[property])));

        setDataSource(sortedData);
        await renderRowData();
    };

    const filteredDataSource = useCallback((): any[] | undefined => {
        if (!props.datasource) return dataSource;

        const filteredDataSource = CodeUtil.isNullOrEmpty(query) ? props.datasource : props.datasource.filter((value) => String(CodeUtil.getPropertyValue(value, sort.columnProperty ?? ''))
            .toLocaleLowerCase()
            .normalize('NFD').replace(/\p{Diacritic}/gu, "")
            .indexOf(query!.toLocaleLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, "").trim()) >= 0);

        return filteredDataSource;
    }, [props.datasource, dataSource, query, sort.columnProperty]);

    const handleRowSelect = useCallback((item: ICodeDataGridRow) => {
        var selected = !!!props.multiselect ? [] : selectedRows.filter(x => x.rowindex !== item.rowindex && x.selected);
        if (item.selected) selected.push(item);
        setSelectedRows(selected);

        props.onRowSelect?.call(this, item);
        props.onRowsSelect?.call(this, selected);
    }, [selectedRows, props, setSelectedRows]);

    const handleSelectAllRows = (select: boolean) => {
        setSelectAll(select);

        if (!select) {
            setSelectedRows([]);
            props.onRowsSelect?.call(this, []);
            return;
        }

        const selected: ICodeDataGridRow[] = [];
        dataSource?.forEach((row, index) => selected.push({ item: row, rowindex: index, selected: true }));
        setSelectedRows(selected);
        props.onRowsSelect?.call(this, selected);
    }

    const renderRowData = useCallback(() => {

        let columns: ICodeDataGridColumn[] = props.columns;

        if (props.columns.length < minColumnCount) {
            let ghostCols = Array<ICodeDataGridColumn>(minColumnCount - props.columns.length).fill({ title: '' });
            columns.push(...ghostCols);
        }

        return (<tbody className={`${props.className ?? ''}`}>
            {filteredDataSource()?.map((item, index) => <CodeDataGridRowItem
                key={index}
                columns={columns}
                striped={props.striped}
                hoverable={props.hoverable}
                readonly={props.readonly}
                item={item}
                selected={selectedRows?.find(x => x.rowindex === index) !== undefined}
                onRowSelect={async (selected) => handleRowSelect({ item, rowindex: index, selected })} />)}
        </tbody>);
    }, [handleRowSelect, selectedRows, filteredDataSource, props]);

    const renderGhostColumns = () => {
        let columns: ICodeDataGridColumn[] = [];
        if (props.columns.length < minColumnCount) {
            let ghostCols = Array<ICodeDataGridColumn>(minColumnCount - props.columns.length).fill({ title: '' });
            columns.push(...ghostCols);
        }
        return (columns.map((_col, index) => <th key={index} scope='col'>{' '}</th>));
    }

    useEffect(() => {
        renderRowData();
    }, [renderRowData]);

    return (
        <StyledCodeDataGrid className={`bg-slate-50/50 rounded-md shadow-md ${props.marginContainer ?? "mt-20 mb-0 mx-8"}`} about={props.height} onContextMenu={props.onContextMenu}>
            <CodeDataGridHeader {...props.header}
                show={props.header !== undefined}
                readonly={props.readonly}
                actions={props.actions}
                simplified={props.simplified}
                onFilter={(query) => setQuery(query)} />

            <div className='overflow-x-auto border-t-2 code-table' onScrollCapture={(e) => tableHeader?.current?.scrollTo({ left: e.currentTarget.scrollLeft })}>
                <table className={`${props.className ?? ''} w-full text-sm text-left text-gray-500`}>
                    <CodeDataGridColumnHeader readOnly={props.readonly}
                        multiselect={props.multiselect}
                        selectedAll={selectAll}
                        onSelectAll={handleSelectAllRows}>
                        <>
                            {props.columns.map((col, index) => <CodeDataGridTextColumn key={index}
                                title={col.title}
                                alignment={col.alignment}
                                sorted={sort.columnIndex === index}
                                onSort={(direction) => handleSortColumn(col.property ?? "", index, direction)} />)}

                            {renderGhostColumns()}
                        </>
                    </CodeDataGridColumnHeader>

                    {renderRowData()}
                </table>
            </div>

            {
                props.hiddenFooter ? <></> :
                    <footer className='footer relative w-full rounded-b-md bottom-0 shadow-md'>
                        <CodeDataGridFooter pagination={props.pagination} />
                    </footer>
            }
        </StyledCodeDataGrid>
    );
};


const StyledCodeDataGrid = styled.div`
    width: 100%;
    height: calc(${props => props.about || "100vh"} - 135px);
    background-color: #fff;
    width: calc(100vw - 4rem);

    .code-table {
        height: calc(${props => props.about || "100%"} - 176px);
    }
`